import React, { useState, ReactNode, useCallback } from 'react';
import { Box, CircularProgress, Typography, Button, Alert, Paper } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useKYCStatus, useKYCSession, getKYCStatusMessage } from '../../hooks/useKYC';
import { KYCBanner, KYCVerificationModal } from '../KYC';

interface KYCRequiredRouteProps {
    children: ReactNode;
    /** If true, allows access even without KYC approval (just shows banner) */
    softBlock?: boolean;
}

/**
 * Route guard that requires KYC verification to be approved.
 *
 * By default (softBlock=false), blocks access to protected content until KYC is approved.
 * With softBlock=true, shows a banner but allows access.
 */
const KYCRequiredRoute: React.FC<KYCRequiredRouteProps> = ({ children, softBlock = false }) => {
    const { isAuthenticated, isLoading: authLoading } = useAuth0();
    const { status, isLoading: kycLoading, error, isKYCRequired, refetch } = useKYCStatus();
    const { session, isCreating, error: sessionError, createSession, refreshSession, clearSession } = useKYCSession();
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    // Handler to start verification - creates session first
    const handleStartVerification = useCallback(async () => {
        setModalError(null);
        await createSession();
        setShowVerificationModal(true);
    }, [createSession]);

    // Handler for modal errors
    const handleModalError = useCallback((error: string) => {
        setModalError(error);
    }, []);

    // Handler for token expiration - refresh the session
    const handleTokenExpired = useCallback(async (): Promise<string> => {
        await refreshSession();
        // Return the new token from the refreshed session
        return session?.accessToken || '';
    }, [refreshSession, session?.accessToken]);

    // Handler to close modal
    const handleCloseModal = useCallback(() => {
        setShowVerificationModal(false);
        setModalError(null);
        clearSession();
    }, [clearSession]);

    // Handler for verification complete
    const handleVerificationComplete = useCallback(() => {
        setShowVerificationModal(false);
        clearSession();
        refetch(); // Refresh KYC status
    }, [clearSession, refetch]);

    // Handler for retry
    const handleRetry = useCallback(async () => {
        setModalError(null);
        await createSession();
    }, [createSession]);

    // Show loading state
    if (authLoading || kycLoading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                sx={{ bgcolor: 'background.default' }}
            >
                <CircularProgress />
            </Box>
        );
    }

    // If not authenticated, let parent ProtectedRoute handle it
    if (!isAuthenticated) {
        return <>{children}</>;
    }

    // If there was an error fetching KYC status, allow access but log it
    if (error) {
        console.error('KYC status check failed:', error);
        // Allow access on error to prevent blocking users
        return <>{children}</>;
    }

    // If KYC is approved, allow access
    if (!isKYCRequired) {
        return <>{children}</>;
    }

    // KYC is required - either soft block (show banner) or hard block (show blocking page)
    if (softBlock) {
        // Soft block: show banner but allow access
        return (
            <>
                <KYCBanner
                    status={status?.status || 'PENDING'}
                    rejectionReason={status?.rejectionReason || null}
                    onStartVerification={handleStartVerification}
                    isLoading={isCreating}
                />
                {children}
                {session?.accessToken && (
                    <KYCVerificationModal
                        open={showVerificationModal}
                        accessToken={session.accessToken}
                        onClose={handleCloseModal}
                        onComplete={handleVerificationComplete}
                        onError={handleModalError}
                        onTokenExpired={handleTokenExpired}
                        errorMessage={modalError || sessionError || undefined}
                        onRetry={handleRetry}
                    />
                )}
            </>
        );
    }

    // Hard block: show full-page KYC required message
    const statusMessage = status?.status ? getKYCStatusMessage(status.status) : 'Verification required';
    const canStartVerification = status?.status === 'PENDING' ||
                                  status?.status === 'ACTION_REQUIRED' ||
                                  status?.status === 'REJECTED' ||
                                  status?.status === 'EXPIRED';

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            sx={{
                bgcolor: 'background.default',
                p: 3
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    maxWidth: 500,
                    width: '100%',
                    p: 4,
                    textAlign: 'center',
                    bgcolor: 'background.paper'
                }}
            >
                <Typography variant="h4" gutterBottom sx={{ color: 'text.primary' }}>
                    Identity Verification Required
                </Typography>

                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {statusMessage}
                </Typography>

                {status?.status === 'REJECTED' && status?.rejectionReason && (
                    <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                        <strong>Reason:</strong> {status.rejectionReason}
                    </Alert>
                )}

                {status?.status === 'IN_REVIEW' && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Your documents are being reviewed. This usually takes 1-2 business days.
                    </Alert>
                )}

                {status?.status === 'ON_HOLD' && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Your verification is under manual review. Please check back later.
                    </Alert>
                )}

                {(status?.status === 'REJECTED_FINAL' || status?.status === 'SUSPENDED') && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        Please contact support at support@radix.trading for assistance.
                    </Alert>
                )}

                {canStartVerification && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleStartVerification}
                        disabled={isCreating}
                        sx={{ mt: 2 }}
                    >
                        {isCreating ? 'Loading...' : (status?.status === 'PENDING' ? 'Start Verification' : 'Retry Verification')}
                    </Button>
                )}

                <Button
                    variant="text"
                    color="inherit"
                    onClick={() => refetch()}
                    sx={{ mt: 2, ml: canStartVerification ? 2 : 0 }}
                >
                    Refresh Status
                </Button>

                {sessionError && (
                    <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                        {sessionError}
                    </Alert>
                )}
            </Paper>

            {session?.accessToken && (
                <KYCVerificationModal
                    open={showVerificationModal}
                    accessToken={session.accessToken}
                    onClose={handleCloseModal}
                    onComplete={handleVerificationComplete}
                    onError={handleModalError}
                    onTokenExpired={handleTokenExpired}
                    errorMessage={modalError || sessionError || undefined}
                    onRetry={handleRetry}
                />
            )}
        </Box>
    );
};

export default KYCRequiredRoute;
